import { IconAlertTriangle, IconExternalLink, IconFileText, IconFileUpload, IconTrash } from "@tabler/icons-react"
import { type FC } from "react"
import { useDropzone } from "react-dropzone"

import {
	type CustomLegalDocumentType,
	customLegalDocumentTypeLabels,
	customLegalDocumentTypes,
} from "@forento/core/models/platform"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import Layout, { PageBreadcrumb } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import ContractWithdrawals from "./ContractWithdrawals"

const maxPdfFileSize = 5 * 1024 * 1024

const LegalSettingsPage: FC = () => {
	const { data, error, refetch } = query.platform.getCustomLegalDocuments.useQuery()
	const update = query.platform.setCustomLegalDocument.useMutation()

	return (
		<Layout>
			<PageBreadcrumb title="Legal" path={[{ title: "Settings", link: routes.settings.index() }]} />
			{error ? (
				<p>Failed to load legal settings.</p>
			) : data === undefined ? (
				<PartialLoadingPage />
			) : (
				<div className="max-w-3xl space-y-4">
					<div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-950">
						<IconAlertTriangle className="mt-0.5 size-5 shrink-0" />
						<p className="text-sm">
							Custom PDFs replace the default public legal pages. Make sure your documents have been
							reviewed and cover your legal obligations before uploading them.
						</p>
					</div>
					<ContractWithdrawals />
					{customLegalDocumentTypes.map(type => (
						<LegalDocumentCard
							key={type}
							type={type}
							document={data.documents.find(document => document.type === type) ?? null}
							isSaving={update.isPending}
							onSave={async document => {
								await update.mutateAsync({ type, document })
								await refetch()
							}}
						/>
					))}
				</div>
			)}
		</Layout>
	)
}

type Props = {
	type: CustomLegalDocumentType
	document: { label: string; content: string } | null
	isSaving: boolean
	onSave(document: { label: string; content: string } | null): Promise<void>
}
const LegalDocumentCard: FC<Props> = ({ type, document, isSaving, onSave }) => {
	const alert = useAlert()
	const toast = useToast()
	const label = customLegalDocumentTypeLabels[type]

	async function handleFile(file: File) {
		if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
			await alert.show("Unsupported file", "Please upload a PDF file.")
			return
		}
		if (file.size > maxPdfFileSize) {
			await alert.show("File too large", "The PDF can be a maximum of 5 MB.")
			return
		}

		const result = await alert.confirm(
			document === null ? "Replace default document" : "Replace custom document",
			`Uploading this PDF will replace the ${document === null ? "default" : "current custom"} **${label}** shown to your users. Make sure the document is correct and legally reviewed before continuing.`,
			{ confirmLabel: "Replace document" },
		)
		if (!result.result) return

		try {
			await onSave({ label: file.name, content: await readFileAsBase64(file) })
			result.close()
			toast.showChangesSaved(label)
		} catch (error) {
			console.error(error)
			result.close()
			await alert.show("Error", "Failed to save the legal document. Please try again later.")
		}
	}

	const dropzone = useDropzone({
		accept: { "application/pdf": [".pdf"] },
		disabled: isSaving,
		multiple: false,
		onDrop(acceptedFiles) {
			const file = acceptedFiles[0]
			if (file !== undefined) void handleFile(file)
		},
	})

	async function handleRemove() {
		const result = await alert.confirm("Remove legal document", `Remove the custom **${label}** PDF?`)
		if (!result.result) return

		try {
			await onSave(null)
			result.close()
			toast.showDeleted(label)
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to remove the legal document. Please try again later.")
		}
	}

	return (
		<section className="border-component-border bg-component-background rounded-3xl border p-6">
			<div className="flex items-start justify-between gap-4">
				<div className="flex min-w-0 items-start gap-3">
					<div className="bg-primary/10 flex size-11 shrink-0 items-center justify-center rounded-lg">
						<IconFileText className="text-primary size-5" />
					</div>
					<div className="min-w-0">
						<h2 className="text-lg font-semibold">{label}</h2>
						<p className="text-sm text-gray-500">
							{document === null ? "Default document" : document.label}
						</p>
					</div>
				</div>
				<span
					className={cn(
						"shrink-0 rounded-full px-3 py-1 text-xs font-semibold",
						document === null ? "bg-gray-100 text-gray-600" : "bg-primary/10 text-primary",
					)}
				>
					{document === null ? "Default" : "Custom"}
				</span>
			</div>
			{document === null ? (
				<div
					{...dropzone.getRootProps({
						"aria-label": `Upload ${label} PDF`,
						"aria-disabled": isSaving,
						className: cn(
							"mt-5 flex min-h-32 w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-[#d7d2e6] bg-[#f9f8ff] p-6 text-center transition-colors",
							isSaving && "cursor-auto opacity-60",
							dropzone.isDragActive && !dropzone.isDragReject && "border-primary bg-primary/5",
							dropzone.isDragReject && "border-danger bg-danger/5",
						),
						role: "button",
						tabIndex: isSaving ? -1 : 0,
					})}
				>
					<input {...dropzone.getInputProps()} />
					<IconFileUpload className="text-primary size-7" />
					<p className="text-primary text-sm font-semibold">
						{dropzone.isDragReject ? "PDF only" : "Upload PDF"}
					</p>
				</div>
			) : (
				<div className="mt-5 flex flex-wrap gap-3">
					<Button variant="secondary" onClick={() => openBase64Pdf(document.content)} isDisabled={isSaving}>
						<IconExternalLink className="size-4" />
						Open
					</Button>
					<div
						{...dropzone.getRootProps({
							"aria-label": `Replace ${label} PDF`,
							"aria-disabled": isSaving,
							className: cn(
								"border-primary text-primary hover:bg-primary/10 inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 bg-white px-4 py-3 text-center text-sm font-semibold transition-colors",
								isSaving && "cursor-auto border-gray-200 text-gray-200",
							),
							role: "button",
							tabIndex: isSaving ? -1 : 0,
						})}
					>
						<input {...dropzone.getInputProps()} />
						<IconFileUpload className="size-4" />
						Replace
					</div>
					<Button variant="secondary-danger" onClick={handleRemove} isDisabled={isSaving}>
						<IconTrash className="size-4" />
						Remove
					</Button>
				</div>
			)}
		</section>
	)
}

function readFileAsBase64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onerror = () => reject(new Error("Failed to read file."))
		reader.onload = () => {
			if (typeof reader.result !== "string") {
				reject(new Error("Failed to read file."))
				return
			}
			const [, content] = reader.result.split(",", 2)
			if (!content) {
				reject(new Error("Failed to read file."))
				return
			}
			resolve(content)
		}
		reader.readAsDataURL(file)
	})
}

function openBase64Pdf(content: string): void {
	const file = new Blob([Uint8Array.from(atob(content), character => character.charCodeAt(0))], {
		type: "application/pdf",
	})
	const fileUrl = URL.createObjectURL(file)
	window.open(fileUrl, "_blank")
	window.setTimeout(() => URL.revokeObjectURL(fileUrl), 60_000)
}

export default LegalSettingsPage
